import { useState } from "react";
import { useRouter } from "next/router";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { createServerSupabaseClient } from "@supabase/auth-helpers-nextjs";
import {
  Box,
  Flex,
  Stack,
  Heading,
  Text,
  Divider,
  Container,
  InputGroup,
  InputRightElement,
  Button,
  SimpleGrid,
  Avatar,
  AvatarGroup,
  useBreakpointValue,
} from "@chakra-ui/react";

import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  accountOptions,
  COLLECTIONS,
  EMAIL_EXIST_MESSAGE,
  FIELDS,
  PROVIDER,
  roleOptions,
} from "utils/constants";
import Header from "components/Header";
import * as ga from "utils/ga";
import { PhoneNumberInput } from "@/components/PhoneNumberInput";
import ErrorText from "@/components/shared/typography/ErrorText";
import BlackButton from "@/components/BlackButton";
import StyledLink from "@/components/StyledLink";
import StyledStack from "@/components/StyledStack";
import StyledHeading from "@/components/StyledHeading";
import StyledInput from "@/components/StyledInput";
import StyledSelect from "@/components/StyledSelect";
import Layout from "@/components/Layout";
import { updateProfile } from "hooks/useProfile";
import { timeNow } from "utils/helpers";

const avatars = [
  {
    name: "Deola",
    url: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ3WDRHDMfgGuNSqc9c-88-FVuST__4MNSP3g&usqp=CAU",
  },
  {
    name: "Dolapo",
    url: "https://i.pinimg.com/originals/6c/85/33/6c8533acc0571b609962987c1365cb5b.jpg",
  },
  {
    name: "Mike",
    url: "https://i.pinimg.com/736x/d0/80/8a/d0808a48625fdca5fbb3762c26b42bc2.jpg",
  },
  {
    name: "Sandra",
    url: "https://pbs.twimg.com/media/D10_LS2XcAAnLCs?format=jpg&name=large",
  },
  {
    name: "Nike",
    url: "https://64.media.tumblr.com/57c58b85d40d85b29e9bb28200c235bc/d4759965f03d7778-81/s1280x1920/6a82828f4b46757616d915e0e952934ae90ef7e0.jpg",
  },
];

const profileSchema = yup.object().shape({
  firstname: yup.string().trim().max(20).nullable().required(),
  lastname: yup.string().trim().max(20).nullable().required(),
  mobile: yup
    .string()
    .min(10)
    .max(16)
    .trim()
    .matches(/^\+[0-9]+$/, "Mobile number is not in correct format")
    .nullable()
    .required(),
  email: yup.string().email().trim().nullable().required(),
  password: yup.string().min(6).trim().nullable().required(),
  account: yup.string().trim().nullable().required(),
  expertise: yup.string().trim().nullable(),
});

const AccountCreateForm = ({
  onCreateAccount,
  isLoading,
  register,
  control,
  errors,
  handleSubmit,
  showRole,
  formError,
}) => {
  const [show, setShow] = useState(false);

  const onShowPassword = () => {
    setShow(!show);
  };

  const avatarSize = useBreakpointValue({ base: "md", md: "lg" });
  const flexWidth = useBreakpointValue({ base: "44px", md: "60px" });
  const flexHeight = useBreakpointValue({ base: "44px", md: "60px" });
  return (
    <Container
      as={SimpleGrid}
      maxW={"7xl"}
      minH={"75vh"}
      columns={{ base: 1, md: 2 }}
      spacing={{ base: 10, lg: 32 }}
      py={{ base: 10, sm: 20, lg: 24 }}
    >
      <Stack spacing={{ base: 10, md: 20 }}>
        <Heading
          lineHeight={1.1}
          fontFamily={"YesevaOne"}
          fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
        >
          Find wellness or fitness professionals, locations, and events near you.
        </Heading>
        <Stack direction={"row"} spacing={4} align={"center"}>
          <AvatarGroup>
            {avatars.map((avatar) => (
              <Avatar
                key={avatar.name}
                name={avatar.name}
                src={avatar.url}
                size={avatarSize}
                position={"relative"}
                zIndex={1}
                _before={{
                  content: '""',
                  width: "full",
                  height: "full",
                  rounded: "full",
                  transform: "scale(1.125)",
                  bgGradient: "linear(to-bl, black,gray.400)",
                  position: "absolute",
                  zIndex: -1,
                  top: 0,
                  left: 0,
                }}
              />
            ))}
          </AvatarGroup>
          <Text fontFamily={"YesevaOne"} fontSize={{ base: "4xl", md: "6xl" }}>
            +
          </Text>
          <Flex
            align={"center"}
            justify={"center"}
            fontFamily={"YesevaOne"}
            fontSize={{ base: "sm", md: "lg" }}
            bg={"gray.800"}
            color={"white"}
            rounded={"full"}
            width={flexWidth}
            height={flexHeight}
            position={"relative"}
            _before={{
              content: '""',
              width: "full",
              height: "full",
              rounded: "full",
              transform: "scale(1.125)",
              bgGradient: "linear(to-bl, gray.400,gray.800)",
              position: "absolute",
              zIndex: -1,
              top: 0,
              left: 0,
            }}
          >
            YOU
          </Flex>
        </Stack>
      </Stack>
      <StyledStack>
        <Stack spacing={4}>
          <StyledHeading
            mainTitle="Create an account"
            subTitle="Discover nearby wellness and fitness professionals, gyms, studios, and events in your city."
          />
          <Text>
            Got an account? <StyledLink href="/login">Log in</StyledLink>
          </Text>
        </Stack>
        <Box as={"form"} mt={10} onSubmit={handleSubmit(onCreateAccount)}>
          <Stack spacing={4}>
            <StyledInput
              id="firstname"
              name="firstname"
              placeholder="First Name"
              isInvalid={errors.firstname}
              register={register}
              errors={errors}
            />
            <StyledInput
              id="lastname"
              name="lastname"
              placeholder="Last Name"
              isInvalid={errors.lastname}
              register={register}
              errors={errors}
            />
            <StyledInput
              id="email"
              name="email"
              type="email"
              placeholder="Email Address"
              isInvalid={errors.email}
              register={register}
              errors={errors}
            />
            <Controller
              control={control}
              name="mobile"
              render={({ field: { onChange } }) => (
                <PhoneNumberInput onChange={onChange} errors={errors} />
              )}
            />
            <StyledSelect
              id="account"
              name="account"
              placeholder="Select Account Type"
              variant="filled"
              isInvalid={errors.account}
              register={register}
              options={accountOptions}
              errors={errors}
            />

            {showRole ? (
              <StyledSelect
                id="expertise"
                name="expertise"
                placeholder="Select Expertise"
                variant="filled"
                isInvalid={errors.expertise}
                register={register}
                options={roleOptions}
                errors={errors}
              />
            ) : null}
            <Divider />
            <InputGroup size="md">
              <StyledInput
                id="password"
                name="password"
                autoComplete="new-password"
                type={show ? "text" : "password"}
                placeholder="Enter Password"
                isInvalid={errors.password}
                register={register}
                errors={errors}
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={onShowPassword}>
                  {show ? "Hide" : "Show"}
                </Button>
              </InputRightElement>
            </InputGroup>
          </Stack>
          <Text fontSize="xs" align="center" py={4}>
            By creating an account, I agree to all of WellFitly&apos;s{" "}
            <StyledLink href={"/terms"}>Terms of Service</StyledLink>, and{" "}
            <StyledLink href={"/privacy"}>Privacy Policy</StyledLink>.
          </Text>
          <BlackButton
            type="submit"
            id="btn-create-account"
            isLoading={isLoading}
            buttonText="Create account"
          />
          <Box p={2}>{formError && <ErrorText text={formError} />}</Box>
        </Box>
      </StyledStack>
    </Container>
  );
};

export default function SignUp() {
  const router = useRouter();
  const [isLoading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const supabaseClient = useSupabaseClient();

  const { watch, register, control, handleSubmit, formState } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: true,
    resolver: yupResolver(profileSchema),
  });
  const { errors } = formState;
  const account = watch("account");

  const canCreateAccount = async (email) => {
    try {
      const { data } = await supabaseClient
        .from(COLLECTIONS.users)
        .select(FIELDS.id)
        .eq(FIELDS.email, email);
      return data?.length === 0;
    } catch (error) {
      throw error;
    }
  };

  const createUserProfile = async (payload) => {
    return await supabaseClient.from(COLLECTIONS.profile).insert(payload);
  };

  const createUserAccount = async ({ email, password, ...metaData }) => {
    try {
      metaData.expertise = account === PROVIDER ? metaData.expertise : "";
      const isProvider = account === PROVIDER ? true : false;
      const {
        data: { user },
        error: userError,
      } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...metaData,
            activation_requested: false,
            is_provider: isProvider,
          },
        },
      });

      if (userError) throw userError;
      const { error: profileError } = await createUserProfile({
        id: user.id,
        email,
        activation_requested: false,
        is_provider: isProvider,
        ...metaData,
      });
      if (profileError) throw profileError;
    } catch (error) {
      ga.event({
        action: "sign_up_error",
      });
      throw error;
    }
  };

  const onCreateAccount = async (formData) => {
    try {
      setFormError(null);
      setLoading(true);
      if (await canCreateAccount(formData.email)) {
        await createUserAccount(formData);
        ga.event({
          action: "sign_up",
          category: "Sign up",
        });
        window.sessionStorage.setItem("hasRegistered", "true");
        router.push(`/sign-up/welcome?referrer=${router.asPath}`);
      } else {
        setFormError(EMAIL_EXIST_MESSAGE);
        setLoading(false);
      }
    } catch (error) {
      ga.event({
        action: "on_create_account_error",
      });
      setLoading(false);
      setFormError(error.message);
    }
  };

  return (
    <Layout>
      <Header browserTitle="WellFitly - Sign up" />
      <Box position={"relative"}>
        <AccountCreateForm
          onCreateAccount={onCreateAccount}
          isLoading={isLoading}
          register={register}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          showRole={account === PROVIDER}
          formError={formError}
        />
      </Box>
    </Layout>
  );
}

export const getServerSideProps = async ({ req, res }) => {
  const supabaseServerClient = createServerSupabaseClient({ req, res });
  const {
    data: { session },
  } = await supabaseServerClient.auth.getSession();

  const {
    data: { user },
  } = await supabaseServerClient.auth.getUser();

  if (session) {
    await updateProfile(supabaseServerClient, user.id, {
      last_login: timeNow(),
    });
    return {
      redirect: {
        permanent: false,
        destination: `/dashboard`,
      },
    };
  }

  return {
    props: {},
  };
};